import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const pad2 = (n) => String(n).padStart(2, '0');

const addHoursToLocalDatetime = (date, time, hoursToAdd) => {
  const d = String(date || '').trim();
  const t = String(time || '').trim();
  const h = Number(hoursToAdd);
  if (!d || !t || !Number.isFinite(h)) return { date: '', time: '' };
  const base = new Date(`${d}T${t}:00`);
  if (Number.isNaN(base.getTime())) return { date: '', time: '' };
  const next = new Date(base.getTime() + h * 60 * 60 * 1000);
  const yyyy = next.getFullYear();
  const mm = pad2(next.getMonth() + 1);
  const dd = pad2(next.getDate());
  const hh = pad2(next.getHours());
  const mi = pad2(next.getMinutes());
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
};

const toDatetimeLocalValue = (date, time) => {
  const d = String(date || '').trim();
  const t = String(time || '').trim();
  if (!d || !t) return '';
  return `${d}T${t}`;
};

const splitDatetimeLocalValue = (value) => {
  const v = String(value || '').trim();
  if (!v || !v.includes('T')) return { date: '', time: '' };
  const [d, t] = v.split('T');
  return { date: String(d || '').trim(), time: String(t || '').trim() };
};

const normalizeEventType = (eventType) => {
  const t = String(eventType || '').trim().toLowerCase();
  if (['lesson', 'training', 'tournament', 'ad'].includes(t)) return t;
  return 'lesson';
};

const labelForType = (eventType) => {
  const t = normalizeEventType(eventType);
  if (t === 'lesson') return '레슨';
  if (t === 'training') return '트레이닝';
  if (t === 'tournament') return '대회';
  return '광고';
};

const generateToken = () => {
  const arr = new Uint8Array(12);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  const fallback = `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
  return fallback.replace(/[^a-f0-9]/gi, '').slice(0, 24).padEnd(24, '0');
};

const EventTypeInviteForm = ({
  supabase,
  eventType,
  initialValues,
  onSubmit,
  submitting,
}) => {
  const type = normalizeEventType(eventType);
  const [form, setForm] = useState({
    title: '',
    date: '',
    endDate: '',
    startTime: '',
    endTime: '',
    locationName: '',
    address: '',
    venueLat: '',
    venueLng: '',
    description: '',
    url: '',
    memberPrice: '',
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    paymentDeadline: '',
    ...(initialValues || {}),
  });

  const [imageDrafts, setImageDrafts] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState(() => {
    const urls = Array.isArray(initialValues?.existingImageUrls) ? initialValues.existingImageUrls : [];
    return urls.map((u) => String(u || '').trim()).filter(Boolean).slice(0, 10);
  });
  const [coverSelection, setCoverSelection] = useState(() => {
    const cover = String(initialValues?.existingCoverImageUrl || '').trim();
    if (cover) return { kind: 'existing', value: cover };
    const urls = Array.isArray(initialValues?.existingImageUrls) ? initialValues.existingImageUrls : [];
    const first = String(urls?.[0] || '').trim();
    return first ? { kind: 'existing', value: first } : null;
  });

  const handleImageSelect = (files) => {
    const selected = Array.from(files || []);
    if (selected.length === 0) return;
    const currentCount = existingImageUrls.length + imageDrafts.length;
    if (currentCount + selected.length > 10) {
      alert('이미지는 최대 10장까지 등록할 수 있습니다.');
      return;
    }
    const nextDrafts = selected.map((file, idx) => ({
      id: `${Date.now()}-${idx}-${file?.name || 'image'}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setImageDrafts((p) => [...(Array.isArray(p) ? p : []), ...nextDrafts]);
    if (!coverSelection && nextDrafts[0]?.id) {
      setCoverSelection({ kind: 'draft', value: nextDrafts[0].id });
    }
  };

  const removeDraft = (id) => {
    setImageDrafts((p) => {
      const next = (Array.isArray(p) ? p : []).filter((d) => d.id !== id);
      if (coverSelection?.kind === 'draft' && coverSelection?.value === id) {
        const fallbackExisting = existingImageUrls[0];
        const fallbackDraft = next[0]?.id;
        setCoverSelection(
          fallbackExisting ? { kind: 'existing', value: fallbackExisting } : fallbackDraft ? { kind: 'draft', value: fallbackDraft } : null
        );
      }
      return next;
    });
  };

  const removeExisting = (url) => {
    const u = String(url || '').trim();
    setExistingImageUrls((p) => {
      const next = (Array.isArray(p) ? p : []).filter((x) => String(x || '').trim() !== u);
      if (coverSelection?.kind === 'existing' && String(coverSelection?.value || '').trim() === u) {
        const fallbackExisting = next[0];
        const fallbackDraft = imageDrafts[0]?.id;
        setCoverSelection(
          fallbackExisting ? { kind: 'existing', value: fallbackExisting } : fallbackDraft ? { kind: 'draft', value: fallbackDraft } : null
        );
      }
      return next;
    });
  };

  const [venueSearchOpen, setVenueSearchOpen] = useState(false);
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [venueSearchResults, setVenueSearchResults] = useState([]);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);
  const [venueSearchError, setVenueSearchError] = useState('');

  const handleVenueSearch = async (query) => {
    const q = String(query || '').trim();
    if (!q) return [];

    const normalize = (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((r) => {
        const roadAddress = String(r?.roadAddress || '').trim();
        const jibunAddress = String(r?.jibunAddress || '').trim();
        const englishAddress = String(r?.englishAddress || '').trim();
        const x = r?.x != null ? Number(r.x) : null;
        const y = r?.y != null ? Number(r.y) : null;
        return {
          roadAddress: roadAddress || null,
          jibunAddress: jibunAddress || null,
          englishAddress: englishAddress || null,
          x: Number.isFinite(x) ? x : null,
          y: Number.isFinite(y) ? y : null,
          placeName: r?.placeName ? String(r.placeName) : null,
        };
      });
    };

    try {
      const { data: placeData } = await supabase.functions.invoke('naver-place-search', {
        body: { query: q, display: 10 },
      });

      if (placeData?.ok) {
        const normalized = normalize(placeData?.results);
        if (normalized.length > 0) return normalized;
      }
    } catch {
      // ignore
    }

    const { data } = await supabase.functions.invoke('naver-geocode', {
      body: { query: q, count: 10, language: 'kor' },
    });

    if (!data?.ok) {
      throw new Error(data?.error || '네이버 지도 검색에 실패했습니다.');
    }

    return normalize(data?.results);
  };

  const openVenueSearch = () => {
    setVenueSearchQuery(String(form?.locationName || form?.address || '').trim());
    setVenueSearchResults([]);
    setVenueSearchError('');
    setVenueSearchOpen(true);
  };

  const runVenueSearch = async () => {
    const q = String(venueSearchQuery || '').trim();
    if (!q) {
      setVenueSearchError('검색어를 입력하세요');
      return;
    }

    setVenueSearchLoading(true);
    setVenueSearchError('');
    try {
      const results = await handleVenueSearch(q);
      setVenueSearchResults(Array.isArray(results) ? results : []);
      if (!Array.isArray(results) || results.length === 0) {
        setVenueSearchError('검색 결과가 없습니다');
      }
    } catch (e) {
      setVenueSearchResults([]);
      setVenueSearchError(e?.message || '검색에 실패했습니다');
    } finally {
      setVenueSearchLoading(false);
    }
  };

  const selectVenue = (row) => {
    const placeName = String(row?.placeName || '').trim();
    const address = String(row?.roadAddress || row?.jibunAddress || '').trim();
    const lat = row?.y != null ? String(row.y) : '';
    const lng = row?.x != null ? String(row.x) : '';

    setForm((p) => ({
      ...(p || {}),
      locationName: placeName || String(p?.locationName || '').trim() || address,
      address,
      venueLat: lat,
      venueLng: lng,
    }));

    setVenueSearchOpen(false);
    setVenueSearchResults([]);
    setVenueSearchError('');
  };

  const canShowUrl = useMemo(() => type === 'ad' || type === 'tournament', [type]);
  const canShowPayment = useMemo(() => type === 'lesson' || type === 'training', [type]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const title = String(form?.title || '').trim();
    const date = String(form?.date || '').trim();
    const endDate = String(form?.endDate || '').trim();
    const startTime = String(form?.startTime || '').trim();
    const endTime = String(form?.endTime || '').trim();
    const venue = String(form?.locationName || '').trim();
    const address = String(form?.address || '').trim();
    const lat = Number.parseFloat(String(form?.venueLat || '').trim());
    const lng = Number.parseFloat(String(form?.venueLng || '').trim());

    if (!title || !date || !startTime || !venue || !address) {
      alert('필수 항목(제목/시작일시/장소/주소)을 입력하세요.');
      return;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      alert('지도 등록을 위해 네이버 검색으로 장소를 선택해주세요.');
      return;
    }

    const normalizedEnd = endDate && endTime ? { endDate, endTime } : addHoursToLocalDatetime(date, startTime, 2);
    const safeEndDate = String(normalizedEnd?.date || '').trim() || date;
    const safeEndTime = String(normalizedEnd?.time || '').trim() || startTime;

    const payload = {
      title,
      sport: 'pickleball',
      date,
      end_date: safeEndDate,
      start_time: startTime,
      end_time: safeEndTime,
      venue,
      venue_address: address,
      venue_lat: lat,
      venue_lng: lng,
      capacity: 0,
      event_type: type,
      cover_image_thumb_url: String(initialValues?.existingCoverImageThumbUrl || '').trim() || null,
      description: String(form?.description || '').trim() || null,
      url: canShowUrl ? (String(form?.url || '').trim() || null) : null,
      ...(canShowPayment
        ? {
            price: Number.isFinite(parseInt(form?.memberPrice)) ? parseInt(form.memberPrice) : 0,
            bank_name: String(form?.bankName || '').trim() || null,
            account_number: String(form?.accountNumber || '').trim() || null,
            account_holder: String(form?.accountHolder || '').trim() || null,
            payment_deadline: form?.paymentDeadline ? new Date(form.paymentDeadline).toISOString() : null,
          }
        : {}),
    };

    await onSubmit({
      payload,
      generateToken,
      images: {
        drafts: imageDrafts,
        keptUrls: existingImageUrls,
        coverSelection,
      },
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">{labelForType(type)} 등록</p>
      </div>

      <form className="p-6 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground caption">기본 정보</p>
          <div className="space-y-3">
            <label className="space-y-1 block">
              <span className="text-sm font-medium text-foreground">제목</span>
              <Input
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder={type === 'ad' ? '광고 제목' : '이벤트 제목'}
                required
              />
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-sm font-medium text-foreground">시작일시</span>
                <input
                  type="datetime-local"
                  value={toDatetimeLocalValue(form.date, form.startTime)}
                  onChange={(e) => {
                    const { date, time } = splitDatetimeLocalValue(e.target.value);
                    setForm((p) => ({
                      ...p,
                      date,
                      startTime: time,
                      endDate: (() => {
                        const hasEnd = Boolean(String(p?.endDate || '').trim()) && Boolean(String(p?.endTime || '').trim());
                        if (!hasEnd) {
                          const next = addHoursToLocalDatetime(date, time, 2);
                          return next?.date || '';
                        }
                        return p?.endDate && p.endDate < date ? date : p?.endDate;
                      })(),
                      endTime: (() => {
                        const hasEnd = Boolean(String(p?.endDate || '').trim()) && Boolean(String(p?.endTime || '').trim());
                        if (!hasEnd) {
                          const next = addHoursToLocalDatetime(date, time, 2);
                          return next?.time || '';
                        }
                        return p?.endTime;
                      })(),
                    }));
                  }}
                  className="w-full h-11 px-3 rounded-lg border border-border bg-background text-sm"
                  required
                />
              </label>

              <label className="space-y-1">
                <span className="text-sm font-medium text-foreground">종료일시</span>
                <input
                  type="datetime-local"
                  value={toDatetimeLocalValue(form.endDate, form.endTime)}
                  onChange={(e) => {
                    const { date, time } = splitDatetimeLocalValue(e.target.value);
                    setForm((p) => ({
                      ...p,
                      endDate: date,
                      endTime: time,
                    }));
                  }}
                  className="w-full h-11 px-3 rounded-lg border border-border bg-background text-sm"
                  min={toDatetimeLocalValue(form.date, form.startTime) || undefined}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground caption">대표 이미지</p>
          <div className="border border-dashed border-border rounded-lg p-3">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleImageSelect(e?.target?.files)}
              className="w-full text-sm text-muted-foreground"
            />
            <p className="text-[11px] text-muted-foreground mt-2">1~10장 업로드 가능 · 대표 이미지 선택 가능</p>
            {(existingImageUrls.length > 0 || imageDrafts.length > 0) && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {existingImageUrls.map((url) => (
                  <div key={url} className="relative">
                    <button
                      type="button"
                      onClick={() => setCoverSelection({ kind: 'existing', value: url })}
                      className={`relative w-full aspect-square rounded-lg overflow-hidden border ${
                        coverSelection?.kind === 'existing' && String(coverSelection?.value || '').trim() === url
                          ? 'border-primary ring-2 ring-primary/40'
                          : 'border-border'
                      }`}
                      title="대표 이미지로 설정"
                    >
                      <img src={url} alt="preview" className="w-full h-full object-cover" />
                      {coverSelection?.kind === 'existing' && String(coverSelection?.value || '').trim() === url && (
                        <span className="absolute left-1 top-1 text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">대표</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeExisting(url)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-background border border-border shadow-elevation-1 flex items-center justify-center text-muted-foreground hover:text-foreground"
                      aria-label="이미지 삭제"
                    >
                      <Icon name="XMarkIcon" size={12} />
                    </button>
                  </div>
                ))}
                {imageDrafts.map((draft) => (
                  <div key={draft.id} className="relative">
                    <button
                      type="button"
                      onClick={() => setCoverSelection({ kind: 'draft', value: draft.id })}
                      className={`relative w-full aspect-square rounded-lg overflow-hidden border ${
                        coverSelection?.kind === 'draft' && coverSelection?.value === draft.id
                          ? 'border-primary ring-2 ring-primary/40'
                          : 'border-border'
                      }`}
                      title="대표 이미지로 설정"
                    >
                      <img src={draft.previewUrl} alt="preview" className="w-full h-full object-cover" />
                      {coverSelection?.kind === 'draft' && coverSelection?.value === draft.id && (
                        <span className="absolute left-1 top-1 text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">대표</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeDraft(draft.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-background border border-border shadow-elevation-1 flex items-center justify-center text-muted-foreground hover:text-foreground"
                      aria-label="이미지 삭제"
                    >
                      <Icon name="XMarkIcon" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground caption">장소</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-sm font-medium text-foreground">장소명</span>
              <div className="flex items-center gap-2">
                <Input
                  value={form.locationName}
                  onChange={(e) => setForm((p) => ({ ...p, locationName: e.target.value }))}
                  placeholder="예: OO체육관"
                />
                <button
                  type="button"
                  onClick={openVenueSearch}
                  className="h-11 px-3 rounded-lg border border-border bg-background hover:bg-muted transition-smooth text-sm font-semibold"
                >
                  검색
                </button>
              </div>
            </label>
            <label className="space-y-1">
              <span className="text-sm font-medium text-foreground">주소</span>
              <Input
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                placeholder="도로명 주소"
              />
            </label>
          </div>
        </div>

        {canShowUrl && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground caption">링크</p>
            <label className="space-y-1 block">
              <span className="text-sm font-medium text-foreground">URL</span>
              <Input
                type="url"
                value={form.url}
                onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                placeholder="https://"
              />
            </label>
          </div>
        )}

        {canShowPayment && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground caption">결제 안내</p>
            <div className="space-y-3 rounded-xl border border-border bg-background p-4">
              <label className="space-y-1 block">
                <span className="text-sm font-medium text-foreground">참가비</span>
                <Input
                  type="number"
                  value={form.memberPrice}
                  onChange={(e) => setForm((p) => ({ ...p, memberPrice: e.target.value }))}
                  placeholder="예: 10000"
                  min="0"
                />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1 block">
                  <span className="text-sm font-medium text-foreground">은행명</span>
                  <Input
                    value={form.bankName}
                    onChange={(e) => setForm((p) => ({ ...p, bankName: e.target.value }))}
                    placeholder="예: KB국민은행"
                  />
                </label>
                <label className="space-y-1 block">
                  <span className="text-sm font-medium text-foreground">계좌번호</span>
                  <Input
                    value={form.accountNumber}
                    onChange={(e) => setForm((p) => ({ ...p, accountNumber: e.target.value }))}
                    placeholder="예: 123-456-789012"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1 block">
                  <span className="text-sm font-medium text-foreground">예금주</span>
                  <Input
                    value={form.accountHolder}
                    onChange={(e) => setForm((p) => ({ ...p, accountHolder: e.target.value }))}
                    placeholder="예: 홍길동"
                  />
                </label>
                <label className="space-y-1 block">
                  <span className="text-sm font-medium text-foreground">결제 마감</span>
                  <input
                    type="datetime-local"
                    value={String(form.paymentDeadline || '')}
                    onChange={(e) => setForm((p) => ({ ...p, paymentDeadline: e.target.value }))}
                    className="w-full h-11 px-3 rounded-lg border border-border bg-background text-sm"
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground caption">상세 설명</p>
          <label className="space-y-1 block">
            <span className="text-sm font-medium text-foreground">설명</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className="w-full min-h-[120px] px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none"
              placeholder={
                type === 'lesson'
                  ? '레슨 대상/레벨/준비물/커리큘럼 등을 입력해주세요.'
                  : type === 'training'
                    ? '훈련 내용/세부 프로그램/대상 등을 입력해주세요.'
                    : type === 'tournament'
                      ? '대회 규정/참가 조건/시상 등을 입력해주세요.'
                      : '광고 내용/노출 기간/요청사항 등을 입력해주세요.'
              }
            />
          </label>
        </div>

        <div className="pt-2">
          <Button type="submit" disabled={submitting} fullWidth>
            {submitting ? '저장 중...' : '저장하기'}
          </Button>
        </div>
      </form>

      {venueSearchOpen && (
        <div
          className="fixed inset-0 z-[10001] flex items-start justify-center pt-[10vh] px-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setVenueSearchOpen(false);
          }}
        >
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
          <div className="relative z-20 w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">장소 검색</h2>
                <p className="text-xs caption text-muted-foreground mt-0.5">주소를 검색하고 선택하세요</p>
              </div>
              <button
                type="button"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic"
                onClick={() => setVenueSearchOpen(false)}
                aria-label="닫기"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <input
                  className="flex-1 px-4 py-3 bg-muted/30 border border-border rounded-md text-foreground"
                  value={venueSearchQuery}
                  onChange={(e) => setVenueSearchQuery(e?.target?.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runVenueSearch();
                    }
                  }}
                  placeholder="예: 서울 서대문구 이화여대길 52, 성남시 분당구 불정로 6"
                />
                <button
                  type="button"
                  onClick={runVenueSearch}
                  disabled={venueSearchLoading}
                  className="h-11 px-4 rounded-lg bg-primary text-primary-foreground font-semibold disabled:opacity-60"
                >
                  검색
                </button>
              </div>

              {venueSearchError && (
                <div className="bg-error/10 border border-error/20 rounded-md p-3 flex items-start gap-2">
                  <Icon name="ExclamationTriangleIcon" size={18} className="mt-0.5 text-error" />
                  <p className="text-sm text-error">{venueSearchError}</p>
                </div>
              )}

              <div className="max-h-[60vh] overflow-y-auto divide-y divide-border border border-border rounded-lg">
                {(venueSearchResults || []).map((row, idx) => {
                  const placeName = String(row?.placeName || '').trim();
                  const address = String(row?.roadAddress || row?.jibunAddress || '').trim();
                  const sub = String(row?.jibunAddress || '').trim();
                  return (
                    <button
                      key={`${address}-${idx}`}
                      type="button"
                      className="w-full text-left p-4 hover:bg-muted transition-athletic"
                      onClick={() => selectVenue(row)}
                    >
                      {placeName && <p className="text-sm font-semibold text-foreground">{placeName}</p>}
                      <p className="text-sm font-medium text-foreground">{address || '주소'}</p>
                      {sub && sub !== address && <p className="text-xs caption text-muted-foreground mt-1">{sub}</p>}
                      <p className="text-xs caption text-muted-foreground mt-1">위치: {row?.y}, {row?.x}</p>
                    </button>
                  );
                })}

                {Array.isArray(venueSearchResults) &&
                  venueSearchResults.length === 0 &&
                  !venueSearchLoading &&
                  !venueSearchError && (
                    <div className="p-4">
                      <p className="text-sm text-muted-foreground">주소 형태로 검색해보세요. (예: "서울 서대문구 이화여대길 52")</p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTypeInviteForm;
